import { N2NRelationshipName } from '@provision/entity-types';
import { ApolloClient } from '@provision/graphql-client';
import { WebSocketEventBus } from '@provision/ui-real-time-events';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { ttlLink } from 'src/graphql/setup/config';
import {
  ADDITIONAL_EVICTION_MAP,
  N2NOperations,
  ONE_TO_MANY_RELATIONS,
} from 'src/graphql/setup/constants';
import { queryRegistry } from 'src/graphql/setup/query-registry';
import { EvictRelevantQueriesParams } from 'src/graphql/setup/types';
import {
  entityMatches,
  isEventRelevantToSearch,
  isEventRelevantToSorting,
  isRecordInCacheList,
  skipUnrelatedUpdateEvents,
} from 'src/graphql/setup/utils';
import { isEntityRelevantToFilters } from 'src/graphql/setup/utils/isEventRelevant';
import { useInvalidateAndRefetch } from './use-invalidate-and-refetch';

/**
 * Custom hook that subscribes to ws events and evicts affected queries from the Apollo cache.
 * Instead of manually updating the cache, this hook evicts the matching query so that when the component remounts,
 * Apollo refetches the data. It also ensures that not queries are invalidated without causing immediate refetching, while active
 * ones would be refetched right away
 */
export const useQueryEvictionSubscription = (
  wsEventBus: WebSocketEventBus,
  apolloClient: ApolloClient<unknown>,
) => {
  // a function that throttles cache eviction
  const invalidateAndRefetch = useInvalidateAndRefetch();

  useEffect(() => {
    const subs = [
      wsEventBus.onOperation('create').subscribe({
        next: (payload) => {
          // normal entity‑level eviction
          evictRelevantQueries({
            ...payload,
            apolloClient,
            operation: 'create',
            invalidateAndRefetch,
          });

          // ONE→MANY logic
          ONE_TO_MANY_RELATIONS.forEach(({ childEntity, parentEntity, fkField }) => {
            const id = payload.attributes[fkField as keyof typeof payload.attributes];

            if (payload.entityName === childEntity && id) {
              evictRelevantQueries({
                invalidateAndRefetch,
                apolloClient,
                operation: 'update',
                entityName: parentEntity,
                attributes: { id },
                previousAttributes: { id },
              });
            }
          });
        },
        error: (err) => console.error('WS create subscription error:', err),
      }),

      // Single subscription for 'update'
      wsEventBus.onOperation('update').subscribe({
        next: (payload) => {
          // normal entity eviction
          evictRelevantQueries({
            ...payload,
            invalidateAndRefetch,
            apolloClient,
            operation: 'update',
          });

          ONE_TO_MANY_RELATIONS.forEach(({ childEntity, parentEntity, fkField }) => {
            if (payload.entityName !== childEntity) return;

            const oldFk =
              payload.previousAttributes?.[fkField as keyof typeof payload.previousAttributes];
            const newFk =
              payload.attributes[fkField as keyof typeof payload.previousAttributes];

            [oldFk, newFk].forEach((fkValue) => {
              if (fkValue) {
                evictRelevantQueries({
                  invalidateAndRefetch,
                  apolloClient,
                  operation: 'update',
                  entityName: parentEntity,
                  attributes: { id: fkValue },
                  previousAttributes: { id: fkValue },
                });
              }
            });
          });
        },
        error: (err) => console.error('WS update subscription error:', err),
      }),
    ];

    Object.entries(ADDITIONAL_EVICTION_MAP).forEach(([relationName, entityMappings]) => {
      N2NOperations.forEach((operation) => {
        subs.push(
          wsEventBus
            .onRelation({ name: relationName as N2NRelationshipName, operation })
            .subscribe({
              next: ({ attributes, previousAttributes }) => {
                entityMappings.forEach(({ entityName, fkField }) => {
                  const idValue =
                    operation === 'createN2NRelationship'
                      ? attributes[fkField as keyof typeof attributes]
                      : previousAttributes[fkField as keyof typeof previousAttributes];

                  if (idValue) {
                    evictRelevantQueries({
                      apolloClient,
                      invalidateAndRefetch,
                      operation: 'update',
                      entityName,
                      attributes: { id: idValue },
                      previousAttributes: { id: idValue },
                    });
                  }
                });
              },
              error: (err) =>
                console.error(`WS ${relationName}.${operation} subscription error:`, err),
            }),
        );
      });
    });

    return () => subs.forEach((s) => s.unsubscribe());
  }, [wsEventBus, apolloClient, invalidateAndRefetch]);
};

/**
 * Evicts queries from the Apollo cache that may be impacted by a received event.
 *
 * This function uses `isEntityRelevantToFilters` to assess the potential relevance of an event by
 * comparing its attributes with the query filters. While this check is a simplified approximation
 * (and does not fully mirror backend filtering logic), it is sufficient for our needs since the only
 * outcome is query invalidation rather than modifying data (i.e., no record insertions or deletions)
 */
function evictRelevantQueries({
  apolloClient,
  entityName,
  attributes,
  previousAttributes,
  operation,
  invalidateAndRefetch,
}: EvictRelevantQueriesParams) {
  const registeredQueries = queryRegistry.getQueriesForEntity(entityName);

  let changedFields = Object.keys(attributes);
  if (operation === 'update' && previousAttributes) {
    changedFields = Object.keys(previousAttributes);
  }

  registeredQueries.forEach((q) => {
    const { query, variables, entityName: qEntity, operationName, timestamp, key } = q;

    // if the query is older than one day, remove it from the registry
    // and stop further processing for this query as it is stale query
    if (dayjs().diff(timestamp, 'hour') > 8) {
      queryRegistry.remove(key);
      ttlLink.invalidateQuery(operationName, variables as Record<string, unknown>);
      return;
    }

    if (skipUnrelatedUpdateEvents({ operation, variables })) {
      return;
    }

    if (!operationName || !entityMatches(qEntity, entityName)) {
      return;
    }

    const id = String(attributes.id);

    const recordExistInTheQuery = isRecordInCacheList({
      apolloClient,
      document: query,
      variables,
      id,
    });

    if (operation === 'create' && recordExistInTheQuery) {
      return;
    }

    const filters = variables.params?.filters ?? variables.filters;
    let filterRelevant = false;
    if (operation === 'update') {
      const filterFields = Array.isArray(filters)
        ? filters.map((f) => f.attribute)
        : Object.keys(filters || {});

      filterRelevant =
        !filterFields.includes('id') && filterFields.some((f) => changedFields.includes(f));
    } else {
      filterRelevant = isEntityRelevantToFilters(attributes, filters);
    }

    const sortingRelevant = isEventRelevantToSorting({
      operation,
      variables,
      changedFields,
      onFirstPage: recordExistInTheQuery,
    });

    const searchRelevant = isEventRelevantToSearch(variables);

    const shouldBeEvicted = filterRelevant || sortingRelevant || searchRelevant;

    if (shouldBeEvicted) {
      invalidateAndRefetch({
        hashKey: key,
        operationName,
        variables: variables as Record<string, string>,
        query,
      });
    }
  });
}
